// @ts-nocheck
import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { Spinner } from 'components/circleSpinner/Spinner';
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;
const PDFViewer = ({
    fileUrl,
    setIsScrolledToEnd,
    showPageBreaks = false,
    containerHeightPx = 350,
    onOpenFullView,
}: {
    fileUrl: string;
    setIsScrolledToEnd?: (isScrolledToEnd: boolean) => void;
    showPageBreaks?: boolean;
    containerHeightPx?: number;
    onOpenFullView?: () => void;
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const pdfRef = useRef(null);
    const [zoomLevel, setZoomLevel] = useState<number>(100);
    const [baseWidthPx, setBaseWidthPx] = useState<number | null>(null);
    const canvasRefs = useRef<HTMLCanvasElement[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const RENDER_SCALE = 1.5;
    useEffect(() => {
        const loadPDF = async () => {
            setLoading(true);
            if (containerRef.current) {
                containerRef.current.innerHTML = '';
            }
            canvasRefs.current = [];
            setBaseWidthPx(null);
            pdfRef.current = await pdfjsLib.getDocument(fileUrl).promise;
            const numPages = pdfRef.current.numPages;
            for (let pageNum = 1; pageNum <= numPages; pageNum++) {
                const page = await pdfRef.current.getPage(pageNum);
                const viewport = page.getViewport({ scale: RENDER_SCALE });
                if (pageNum === 1) {
                    // 100% zoom should mean "actual page size" (or fit the
                    // available width, if the box is narrower than that),
                    // not "stretched to fill whatever width the box has".
                    const naturalWidth = viewport.width / RENDER_SCALE;
                    const availableWidth = scrollContainerRef.current?.clientWidth || naturalWidth;
                    setBaseWidthPx(Math.min(naturalWidth, availableWidth));
                }
                const canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                canvas.style.width = '100%';
                canvas.style.height = 'auto';
                canvas.style.margin = '0';
                canvas.style.padding = '0';
                canvas.style.display = 'block';
                if (showPageBreaks) {
                    // Make each page feel like a physical sheet when zoomed out.
                    canvas.style.backgroundColor = '#ffffff';
                    canvas.style.border = '1px solid #d8dee8';
                    canvas.style.boxShadow = '0 1px 3px rgba(17, 24, 39, 0.12)';
                }
                const context = canvas.getContext('2d');
                const renderContext = {
                    canvasContext: context,
                    viewport,
                };
                await page.render(renderContext).promise;

                // Trim empty whitespace from the last page
                if (pageNum === numPages && context) {
                    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
                    const pixels = imageData.data;
                    let lastContentRow = 0;
                    for (let y = canvas.height - 1; y >= 0; y--) {
                        let rowHasContent = false;
                        for (let x = 0; x < canvas.width; x++) {
                            const idx = (y * canvas.width + x) * 4;
                            // Check if pixel is not white (allowing small tolerance)
                            if (pixels[idx] < 250 || pixels[idx + 1] < 250 || pixels[idx + 2] < 250) {
                                rowHasContent = true;
                                break;
                            }
                        }
                        if (rowHasContent) {
                            lastContentRow = y;
                            break;
                        }
                    }
                    // Add some padding below the last content row
                    const trimmedHeight = Math.min(lastContentRow + 40, canvas.height);
                    if (trimmedHeight < canvas.height) {
                        const trimmedCanvas = document.createElement('canvas');
                        trimmedCanvas.width = canvas.width;
                        trimmedCanvas.height = trimmedHeight;
                        trimmedCanvas.style.width = '100%';
                        trimmedCanvas.style.height = 'auto';
                        trimmedCanvas.style.margin = '0';
                        trimmedCanvas.style.padding = '0';
                        trimmedCanvas.style.display = 'block';
                        if (showPageBreaks) {
                            trimmedCanvas.style.backgroundColor = '#ffffff';
                            trimmedCanvas.style.border = '1px solid #d8dee8';
                            trimmedCanvas.style.boxShadow = '0 1px 3px rgba(17, 24, 39, 0.12)';
                        }
                        const trimmedCtx = trimmedCanvas.getContext('2d');
                        if (trimmedCtx) {
                            trimmedCtx.drawImage(canvas, 0, 0, canvas.width, trimmedHeight, 0, 0, canvas.width, trimmedHeight);
                            canvasRefs.current.push(trimmedCanvas);
                            containerRef.current?.appendChild(trimmedCanvas);
                            continue;
                        }
                    }
                }
                canvasRefs.current.push(canvas);
                containerRef.current?.appendChild(canvas);

                if (showPageBreaks && pageNum < numPages) {
                    const divider = document.createElement('div');
                    // Paper-like gap to make page boundaries obvious.
                    divider.style.height = '14px';
                    divider.style.backgroundColor = '#edf1f6';
                    divider.style.borderTop = '1px solid #dde3ec';
                    divider.style.borderBottom = '1px solid #c8d0dc';
                    containerRef.current?.appendChild(divider);
                }
            }
            setLoading(false);
        };
        loadPDF();
    }, [fileUrl, showPageBreaks]);

    const handleScroll = (event: { target: any }) => {
        if (setIsScrolledToEnd) {
            const target = event.target;
            const scrollY = target.scrollTop;
            const innerHeight = target.clientHeight;
            const scrollHeight = target.scrollHeight;
            if (scrollY + innerHeight + 20 >= scrollHeight) {
                setIsScrolledToEnd(true);
            }
        }
    };
    return (
        <div style={{ position: 'relative' }}>
              {loading && (
                <div
                    style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 10,
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        padding: '10px',
                        borderRadius: '5px',
                    }}
                >
                    <Spinner />
                </div>
            )}
            <div
                ref={scrollContainerRef}
                style={{
                    height: `${containerHeightPx}px`,
                    maxHeight: '60vh',
                    overflow: 'auto',
                    overscrollBehavior: 'contain',
                    WebkitOverflowScrolling: 'touch',
                    border: '1px solid #cccccc',
                    borderRadius: '10px',
                    padding: '0px',
                    fontSize: '0.9rem',
                    marginBottom: '20px',
                }}
                onScroll={handleScroll}
            >
                <div
                    ref={containerRef}
                    id="pdf-container"
                    style={{
                        width: baseWidthPx ? `${(baseWidthPx * zoomLevel) / 100}px` : '100%',
                        margin: '0 auto',
                        padding: '0',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                ></div>
            </div>
            <div
                style={{
                    position: 'absolute',
                    bottom: '20px',
                    right: '20px',
                    zIndex: 10,
                    padding: '5px',
                    borderRadius: '5px',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: '5px',
                    pointerEvents: 'none',
                }}
            >
                <button
                    type="button"
                    onClick={onOpenFullView}
                    style={{
                        display: onOpenFullView ? 'inline-flex' : 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(17, 24, 39, 0.75)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        padding: '6px 10px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        pointerEvents: 'auto',
                        whiteSpace: 'nowrap',
                    }}
                    title="Open agreement in full view"
                    aria-label="Open agreement in full view"
                >
                    ⤢ Open full view
                </button>
                <button
                    type="button"
                    onClick={() => setZoomLevel((prev) => Math.min(prev + 10, 300))}
                    style={{
                        backgroundColor: 'rgba(0, 0, 255, 0.5)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        padding: '2px 10px',
                        cursor: 'pointer',
                        fontSize: '20px',
                        pointerEvents: 'auto',
                    }}
                >
                    +
                </button>
                <button
                    type="button"
                    onClick={() => setZoomLevel((prev) => Math.max(prev - 10, 10))}
                    style={{
                        backgroundColor: 'rgba(0, 0, 255, 0.5)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        padding: '2px 10px',
                        cursor: 'pointer',
                        fontSize: '20px',
                        pointerEvents: 'auto',
                    }}
                >
                    -
                </button>
            </div>
        </div>
    );
};
export default PDFViewer;
